import axios from "axios";
import CrudApi from "./index";
import { store } from "@src/redux/store";
import { getRefreshToken, getUserToken } from "../helpers";
import { setTokens, clearAuth } from "@src/views/Auth/User/Login/store";
import { decryptResponseProps } from "./encryption";

let refreshPromise = null;

// redux-persist does not sync between tabs, so a tab that slept through another
// tab's refresh would keep using a revoked token until it got logged out.
const tokensChannel =
  typeof BroadcastChannel === "undefined"
    ? null
    : new BroadcastChannel(`${process.env.REACT_APP_REDUX_INDEXED_DB_NAME}-auth-tokens`);

if (tokensChannel) {
  tokensChannel.onmessage = (event) => {
    const { access_token, refresh_token } = event?.data || {};
    if (access_token && refresh_token && access_token !== getUserToken()) {
      store.dispatch(setTokens({ access_token, refresh_token }));
    }
  };
}

const requestNewTokens = async () => {
  const refresh_token = getRefreshToken();
  if (!refresh_token) {
    throw new Error("No refresh token available");
  }

  const response = await CrudApi({ useFormData: false, useFilteredBranches: false }).post("/api/auth/refresh-token", { refresh_token });
  const { access_token, refresh_token: newRefreshToken } = response?.data?.data || {};

  if (response?.status === 200 && access_token && newRefreshToken) {
    store.dispatch(setTokens({ access_token, refresh_token: newRefreshToken }));
    tokensChannel?.postMessage({ access_token, refresh_token: newRefreshToken });
    return access_token;
  }

  throw new Error("Token refresh failed");
};

const getNewToken = () => {
  if (!refreshPromise) {
    refreshPromise = requestNewTokens().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

export const refreshAuthAndRetry = async (error) => {
  const config = error.config;
  const requestAuthorization = config?.headers?.Authorization || config?.headers?.authorization;
  const requestToken = requestAuthorization?.replace(/^Bearer\s+/i, "");
  const currentToken = getUserToken();
  let token;

  if (currentToken && requestToken && currentToken !== requestToken) {
    token = currentToken;
  } else {
    try {
      token = await getNewToken();
    } catch (refreshError) {
      // A rejected refresh token is already handled by the session guard on the
      // response interceptor; this only covers having no refresh token to send.
      if (!getRefreshToken()) {
        store.dispatch(clearAuth());
      }
      throw refreshError;
    }
  }

  config.headers = { ...config.headers, Authorization: `Bearer ${token}` };

  const response = await axios(config);
  return decryptResponseProps(response);
};
